import { Button } from '@/components/Button';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormInput } from '@/components/FormInput';
import { useState } from 'react';
import { AuthMessage } from './AuthMessages';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from '@/components/Link';
import { Logo } from '@/components/Logo';
import { useProfileQuery } from '@/models/user';
import { useLoginMutation } from '@/models/auth';
import { zodResolver } from '@hookform/resolvers/zod';
import { z, ZodType } from 'zod';
import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router';

type FormValues = {
  email: string;
  password: string;
};

export const LoginForm = () => {
  const [login, { isLoading }] = useLoginMutation();
  const [errorCode, setErrorCode] = useState<number | undefined>();
  const { t } = useTranslation();
  const [cookies, setCookie] = useCookies(['token']);
  const navigate = useNavigate();
  const { refetch } = useProfileQuery(undefined, {
    skip: !cookies.token,
  });

  const schema: ZodType<FormValues> = z.object({
    email: z
      .string({
        required_error: t('auth.error.email_empty'),
      })
      .email({
        message: t('auth.error.email_not_valid'),
      }),
    password: z
      .string({
        required_error: t('auth.error.password_empty'),
      })
      .min(3, {
        message: t('auth.error.password_not_valid'),
      }),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onSubmit',
  });

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      const response = await login(data).unwrap();
      if (response?.result.accessToken) {
        setCookie('token', response.result.accessToken);
        await refetch();
        navigate('/');
      }
    } catch (err: any) {
      setErrorCode(err.data?.code);
    }
  };
  // TODO: add helper with warnings handling

  return (
    <div className="bg-white flex flex-col items-center justify-center w-full h-full sm:h-auto sm:w-96 sm:rounded-xl xl:w-[650px] xl:h-[650px] xl:rounded-full shrink-0">
      <div className="w-full xl:w-96 p-7">
        <div className="flex justify-center mb-5">
          <Logo />
        </div>
        <div className="flex flex-col items-center w-full">
          <span className="mb-2 text-xl font-bold">
            <Trans i18nKey="auth.login.form.title" />
          </span>

          <div className="mb-6">
            <span className="mr-2 text-base">
              <Trans i18nKey="auth.login.form.no_account" />
            </span>
            <Link to="/register">
              <Trans i18nKey="auth.login.form.signup" />
            </Link>
          </div>

          {errorCode && <AuthMessage error={errorCode} />}

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col items-center justify-center w-full gap-1"
          >
            <FormInput
              name="email"
              label={t('auth.login.form.email')}
              control={control}
              error={errors.email}
              variant="outlined"
              fullWidth
            />
            <FormInput
              name="password"
              label={t('label.password')}
              control={control}
              error={errors.password}
              variant="outlined"
              fullWidth
              secured
            />
            <div className="flex items-center justify-end w-full mb-2 text-sm">
              <Link to="/forgot-password">
                <Trans i18nKey="auth.login.form.forgot_password" />
              </Link>
            </div>

            <Button
              variant="contained"
              fullWidth
              type="submit"
              loading={isLoading}
            >
              <Trans i18nKey="auth.login.form.login" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};
